import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Shows one toast per thing that went wrong.
 *
 * <p>This used to suppress a toast only if the previous one was less than 10 milliseconds old,
 * which is short enough that it almost never matched: a page that fires ten requests and gets ten
 * failures showed ten toasts stacked on top of each other, and the user had to dismiss all of them
 * to see the screen. The window is three seconds now, and it is keyed on the message, so a second,
 * genuinely different failure still gets through instead of being swallowed by the first.
 */
const DUPLICATE_WINDOW_MS = 3000;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    private lastMessage: string | null = null;
    private lastShownAt = 0;

    constructor(private toastr: ToastrService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((err: any) => {
                const message = err?.error?.message ? err.error.message : 'An unexpected error occurred.';

                if (!this.isRepeatOfRecent(message)) {
                    this.lastMessage = message;
                    this.lastShownAt = Date.now();
                    this.toastr.error(message, 'Error');
                }

                return throwError(() => err);
            }),
        );
    }

    private isRepeatOfRecent(message: string): boolean {
        return message === this.lastMessage && Date.now() - this.lastShownAt < DUPLICATE_WINDOW_MS;
    }
}
